'use client';

import { PluginConfigData } from '@/types/plugin-config';
import { usePluginConfig, useTranslation } from '@tagadapay/plugin-sdk/v2';
import { ArrowLeft, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';

interface TopBarProps {
  onGoToShop?: () => void;
  className?: string;
  hideGoToShop?: boolean;
}

export function TopBar({ onGoToShop, className = '', hideGoToShop = false }: TopBarProps) {
  const { t } = useTranslation();
  const { config } = usePluginConfig<PluginConfigData>();

  const companyName = config?.header?.companyName || 'Your Store';
  const showLogo = config?.header?.showLogo ?? false;
  const logoUrl = config?.header?.logoUrl;
  const goToShopText = config?.header?.goToShopText;
  const goToShopTextColor = config?.header?.goToShopTextColor || '#000000';
  const borderColor = config?.header?.borderColor || '#e5e7eb';

  const handleGoToShop = () => {
    if (onGoToShop) {
      onGoToShop();
      return;
    }
    const overrideUrl = config?.header?.goToShopUrl;
    if (overrideUrl) {
      window.location.href = overrideUrl;
      return;
    }
    window.history.back();
  };

  return (
    <div
      className={`relative border-b bg-[var(--header-background-color)] ${className}`}
      style={{ borderColor }}
      editor-id="config.header.backgroundImage config.header.backgroundColor config.header.borderColor"
    >
      {config?.header?.backgroundImage && (
        <img
          className="z-1 absolute inset-0 h-full w-full object-cover"
          src={config?.header?.backgroundImage || ''}
          alt="Header Background"
        />
      )}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex min-h-16 items-center justify-between py-5">
          {/* Logo/Company Name */}
          <div
            className="z-10 flex items-center"
            editor-id="config.header.logoHeight config.header.companyName config.header.logoUrl config.header.showLogo"
          >
            {showLogo && logoUrl ? (
              <img
                src={logoUrl}
                alt={companyName}
                className="h-10 w-auto"
                style={{
                  height: config?.header?.logoHeight ? `${config?.header?.logoHeight}px` : undefined,
                }}
                loading="eager"
                fetchPriority="high"
                onError={(e) => {
                  // Fallback to company name if logo fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) {
                    fallback.style.display = 'block';
                  }
                }}
              />
            ) : null}
            {(!showLogo || !logoUrl) && (
              <span className="text-xl font-semibold text-gray-900">{companyName}</span>
            )}
          </div>

          {/* Go to Shop Button */}
          {!hideGoToShop && (
            <Button
              variant="ghost"
              onClick={handleGoToShop}
              className="z-10 flex items-center space-x-2 bg-[var(--header-background-color)] font-semibold hover:bg-gray-200/50"
              style={{ color: goToShopTextColor }}
              editor-id="config.header.goToShopTextColor"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline" editor-id="config.header.goToShopText">
                {t(goToShopText, 'Go to shop')}
              </span>
              <ShoppingBag className="h-4 w-4 sm:hidden" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
